import React, { useEffect, useState, useCallback } from "react";
import { Handshake, Loader2, ExternalLink, TrendingUp, RefreshCw } from "lucide-react";
import api from "@/lib/api";

const PARTNER_META = {
  mullvad:      { name: "Mullvad VPN",   kind: "VPN",     accent: "#FFD700" },
  proton:       { name: "Proton VPN",    kind: "VPN",     accent: "#6D4AFF" },
  dediseedbox: { name: "DediSeedbox",   kind: "Seedbox", accent: "#2BD9A5" },
  rapidseedbox: { name: "RapidSeedbox",  kind: "Seedbox", accent: "#FF6B35" },
};

function fmtAgo(iso) {
  if (!iso) return "never";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "never";
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export default function AdminPartners() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/admin/partners/stats", { params: { days } });
      setData(d);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const rows = data?.rows || [];
  const maxTotal = Math.max(1, ...rows.map((r) => r.total));

  return (
    <section className="mt-10" data-testid="admin-partners-section">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <h2 className="font-display text-xl font-bold flex items-center gap-2">
          <Handshake className="w-5 h-5 text-[var(--tt-primary)]" />
          Affiliate Partners
        </h2>
        <span className="text-[10px] uppercase tracking-wider bg-[var(--tt-primary)]/15 text-[var(--tt-primary)] px-2 py-0.5 rounded font-semibold">
          {data?.grand_total ?? 0} all-time clicks
        </span>
        <div className="ml-auto flex items-center gap-2">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            data-testid="admin-partners-window"
            className="bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded-full px-3 py-1.5 text-xs outline-none cursor-pointer hover:border-[var(--tt-primary)]"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last 365 days</option>
          </select>
          <button
            onClick={load}
            data-testid="admin-partners-refresh"
            className="p-1.5 rounded-full bg-white/5 hover:bg-white/10 text-[var(--tt-text-muted)] hover:text-white"
            title="Refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      <div className="bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-2xl overflow-hidden">
        {loading && !data ? (
          <div className="py-12 flex items-center justify-center text-[var(--tt-text-muted)] text-sm gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading stats…
          </div>
        ) : rows.length === 0 ? (
          <div className="py-10 text-center text-[var(--tt-text-muted)] text-sm">
            No partner clicks recorded yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-partners-table">
              <thead className="bg-black/40 text-[10px] uppercase tracking-wider text-[var(--tt-text-muted)]">
                <tr>
                  <th className="text-left px-4 py-3">Partner</th>
                  <th className="text-left px-4 py-3">Type</th>
                  <th className="text-right px-4 py-3">Last 24h</th>
                  <th className="text-right px-4 py-3">Last 7d</th>
                  <th className="text-left px-4 py-3 w-1/3">Window total ({data?.window_days}d)</th>
                  <th className="text-right px-4 py-3">Unique</th>
                  <th className="text-right px-4 py-3">Last click</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const meta = PARTNER_META[r.partner_id] || { name: r.partner_id, kind: "—", accent: "#888" };
                  const pct = (r.total / maxTotal) * 100;
                  return (
                    <tr
                      key={r.partner_id}
                      className="border-t border-[var(--tt-border)] hover:bg-white/[0.02]"
                      data-testid={`admin-partner-row-${r.partner_id}`}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          <span
                            className="w-2.5 h-2.5 rounded-full shrink-0"
                            style={{ background: meta.accent, boxShadow: `0 0 8px ${meta.accent}` }}
                          />
                          <span className="font-semibold">{meta.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-[10px] uppercase font-bold tracking-wider text-[var(--tt-text-muted)]">
                        {meta.kind}
                      </td>
                      <td className="px-4 py-3 text-right font-mono-tt">
                        {r.last_24h > 0 ? (
                          <span className="text-emerald-400 inline-flex items-center gap-1 justify-end">
                            <TrendingUp className="w-3 h-3" /> {r.last_24h}
                          </span>
                        ) : <span className="text-[var(--tt-text-faint)]">0</span>}
                      </td>
                      <td className="px-4 py-3 text-right font-mono-tt">{r.last_7d}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-1.5 bg-white/5 rounded-full overflow-hidden min-w-[120px]">
                            <div
                              className="h-full rounded-full transition-all"
                              style={{
                                width: `${pct}%`,
                                background: `linear-gradient(90deg, ${meta.accent}, ${meta.accent}99)`,
                                boxShadow: `0 0 12px ${meta.accent}55`,
                              }}
                            />
                          </div>
                          <span className="font-mono-tt text-sm font-bold w-12 text-right">{r.total}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right font-mono-tt text-[var(--tt-text-muted)]">
                        {r.unique_visitors}
                      </td>
                      <td className="px-4 py-3 text-right font-mono-tt text-[10px] text-[var(--tt-text-muted)]">
                        {fmtAgo(r.last_click)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="mt-3 flex items-center justify-between gap-3 text-[11px] text-[var(--tt-text-faint)] font-mono-tt flex-wrap">
        <span>
          Tracking IDs hashed daily — no raw IPs stored. Edit affiliate URLs in <code className="text-[var(--tt-text-muted)]">SoftwarePage.jsx</code>.
        </span>
        <a
          href="/software"
          target="_blank"
          rel="noreferrer noopener"
          data-testid="admin-partners-open-page"
          className="inline-flex items-center gap-1 text-[var(--tt-primary)] hover:text-white"
        >
          Open Software page <ExternalLink className="w-3 h-3" />
        </a>
      </div>
    </section>
  );
}
